<!--
 * @Descripttion: 选择开通场景弹窗
 * @Date: 2026-07-02 10:00:00
-->
<template>
  <BaseDialog
    width="980px"
    title="选择开通场景"
    :model-value="true"
    :show-close="true"
    :close-on-press-escape="true"
    :close-on-click-modal="false"
    :show-footer="false"
  >
    <div class="picker-wrap" v-xrk-loading="loading">
      <div class="picker-toolbar">
        <BaseInput
          v-model="keyword"
          clearable
          placeholder="搜索一级 / 二级 / 三级场景"
          style="flex: 1"
        />
        <BaseButton @click="onSelectAll(true)">全选</BaseButton>
        <BaseButton @click="onSelectAll(false)">清空</BaseButton>
      </div>
      <div class="picker-tip">
        已选择
        <span class="picker-count">{{ selectedIds.size }}</span>
        个场景；工业价默认等于平台价，可超出平台区间（确定时需二次确认），数字区间须由低到高。
      </div>
      <BaseTable :data="pagedScenes" :columns="columns" />
      <BasePagination
        class="picker-pagination"
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="[10, 15, 30, 50]"
        :layout="['total', 'sizes', 'prev', 'pager', 'next', 'jumper']"
        :total="filteredScenes.length"
        @current-change="onPageChange"
        @size-change="onPageChange"
      />
    </div>

    <template #footer>
      <BaseButton @click="onCancel">取消</BaseButton>
      <BaseButton type="primary" :loading="saving" @click="onConfirm">
        确定
      </BaseButton>
    </template>
  </BaseDialog>
</template>

<script lang="tsx" setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import {
  BaseButton,
  BaseCheckbox,
  BaseDialog,
  BaseInput,
  BasePagination,
  BaseTable,
  BaseTableProps,
  useTableColumn,
  XrkMessage,
  XrkMessageBox
} from 'xrk-components';
import { getIndustryPriceTree, saveScenes } from '../data-source/apis';
import type { ScenePriceSceneItem } from '../data-source/types';
import {
  formatPriceInputText,
  formatPriceRange,
  getEffectiveIndustryRange,
  getPlatformRangeWarning,
  hasRangeError,
  isOutOfPlatformRange,
  validatePriceRange
} from '../utils/price-range';
import { MAX_PRICE, MIN_PRICE, PRICE_DECIMALS } from '../constants';

const emits = defineEmits(['confirm']);

const props = withDefaults(
  defineProps<{
    remove?: () => void;
    industryId?: string | number;
    scenes?: ScenePriceSceneItem[];
  }>(),
  {
    remove() {
      return () => {
        // auto-injected by useCreateModal
      };
    },
    industryId: '',
    scenes: () => []
  }
);

type PickerRow = ScenePriceSceneItem & {
  minText: string;
  maxText: string;
  minError: string;
  maxError: string;
  minWarn: string;
  maxWarn: string;
};

const loading = ref(false);
const saving = ref(false);
const keyword = ref('');
const currentPage = ref(1);
const pageSize = ref(15);
const pickerRows = ref<PickerRow[]>([]);
const selectedIds = reactive(new Set<string>());

const toPickerRow = (scene: ScenePriceSceneItem): PickerRow => {
  const platform: [number, number] = [
    scene.platformPriceLower,
    scene.platformPriceUpper
  ];
  const effective = getEffectiveIndustryRange(
    platform,
    scene.industryPriceLower,
    scene.industryPriceUpper
  );
  return {
    ...scene,
    minText: formatPriceInputText(effective[0]),
    maxText: formatPriceInputText(effective[1]),
    minError: '',
    maxError: '',
    minWarn: '',
    maxWarn: ''
  };
};

/** 合并接口数据与页面未保存的本地修改 */
const mergeScenes = (
  apiScenes: ScenePriceSceneItem[],
  localScenes: ScenePriceSceneItem[]
) => {
  const localMap = new Map(localScenes.map(item => [item.id, item]));
  return apiScenes.map(apiScene => {
    const local = localMap.get(apiScene.id);
    if (!local) {
      return apiScene;
    }
    return {
      ...apiScene,
      enabled: local.enabled,
      industryPriceLower: local.industryPriceLower,
      industryPriceUpper: local.industryPriceUpper
    };
  });
};

const syncSelectedIds = (scenes: ScenePriceSceneItem[]) => {
  selectedIds.clear();
  scenes
    .filter(item => item.enabled)
    .forEach(item => {
      selectedIds.add(item.id);
    });
};

const fetchScenes = async () => {
  loading.value = true;
  try {
    const industryId =
      props.industryId !== '' && props.industryId != null
        ? Number(props.industryId)
        : undefined;
    const apiScenes = await getIndustryPriceTree(industryId);
    const mergedScenes = mergeScenes(apiScenes, props.scenes);
    pickerRows.value = mergedScenes.map(toPickerRow);
    syncSelectedIds(mergedScenes);
  } catch (error) {
    console.error('获取开通场景列表失败:', error);
    XrkMessage.error('获取开通场景列表失败');
  } finally {
    loading.value = false;
  }
};

const filteredScenes = computed(() => {
  const q = keyword.value.trim();
  if (!q) {
    return pickerRows.value;
  }
  return pickerRows.value.filter(item => item.scenePath.includes(q));
});

/** 当前页数据 */
const pagedScenes = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  return filteredScenes.value.slice(start, start + pageSize.value);
});

/** 分页切换（BasePagination 通过事件回传页码，而非 v-model） */
const onPageChange = (payload: { currentPage?: number; pageSize?: number }) => {
  if (payload.pageSize != null) {
    pageSize.value = payload.pageSize;
  }
  if (payload.currentPage != null) {
    currentPage.value = payload.currentPage;
  }
};

/** 搜索条件变化时回到第一页 */
watch(keyword, () => {
  currentPage.value = 1;
});

const validateRow = (row: PickerRow, required: boolean) => {
  const platform: [number, number] = [
    row.platformPriceLower,
    row.platformPriceUpper
  ];
  const err = validatePriceRange(row.minText, row.maxText, platform, required);
  row.minError = err.min;
  row.maxError = err.max;

  if (!required || hasRangeError(err)) {
    row.minWarn = '';
    row.maxWarn = '';
  } else {
    const warn = getPlatformRangeWarning(row.minText, row.maxText, platform);
    row.minWarn = warn.min;
    row.maxWarn = warn.max;
  }

  return !hasRangeError(err);
};

const columns: BaseTableProps['columns'] = [
  useTableColumn({
    label: '',
    prop: 'checked',
    width: 60,
    render: ({ row }) => (
      <BaseCheckbox
        modelValue={selectedIds.has(row.id)}
        onUpdate:modelValue={(checked: boolean) => onToggle(row.id, checked)}
      />
    )
  }),
  useTableColumn({
    label: '场景路径',
    prop: 'scenePath',
    minWidth: 320
  }),
  useTableColumn({
    label: '平台价格区间',
    prop: 'platformPrice',
    width: 160,
    renderHeader: () => (
      <>
        <div style="text-align: center;">
          <div>项目结算单价</div>
          <div>（平台价格区间）</div>
        </div>
      </>
    ),
    render: ({ row }) => (
      <span>
        {formatPriceRange(row.platformPriceLower, row.platformPriceUpper)}
      </span>
    )
  }),
  useTableColumn({
    prop: 'industryPriceLower',
    width: 160,
    renderHeader: () => (
      <>
        <div style="text-align: center;">
          <div>项目结算单价</div>
          <div>（工业价下限）</div>
        </div>
      </>
    ),
    render: ({ row }) => (
      <div>
        <BaseInput
          type="number"
          min={MIN_PRICE}
          max={MAX_PRICE}
          decimals={PRICE_DECIMALS}
          placeholder="下限"
          v-model={row.minText}
          onInput={() => validateRow(row, selectedIds.has(row.id))}
        />
        {row.minError ? (
          <div class="field-error">{row.minError}</div>
        ) : row.minWarn ? (
          <div class="field-warn">{row.minWarn}</div>
        ) : null}
      </div>
    )
  }),
  useTableColumn({
    prop: 'industryPriceUpper',
    width: 160,
    renderHeader: () => (
      <>
        <div style="text-align: center;">
          <div>项目结算单价</div>
          <div>（工业价上限）</div>
        </div>
      </>
    ),
    render: ({ row }) => (
      <div>
        <BaseInput
          type="number"
          min={MIN_PRICE}
          max={MAX_PRICE}
          decimals={PRICE_DECIMALS}
          placeholder="上限"
          v-model={row.maxText}
          onInput={() => validateRow(row, selectedIds.has(row.id))}
        />
        {row.maxError ? (
          <div class="field-error">{row.maxError}</div>
        ) : row.maxWarn ? (
          <div class="field-warn">{row.maxWarn}</div>
        ) : null}
      </div>
    )
  })
];

const onToggle = (id: string, checked: boolean) => {
  if (checked) {
    selectedIds.add(id);
  } else {
    selectedIds.delete(id);
  }
  const row = pickerRows.value.find(item => item.id === id);
  if (row) {
    validateRow(row, checked);
  }
};

const onSelectAll = (checked: boolean) => {
  filteredScenes.value.forEach(row => {
    if (checked) {
      selectedIds.add(row.id);
    } else {
      selectedIds.delete(row.id);
    }
    validateRow(row, checked);
  });
};

const onCancel = () => {
  props.remove();
};

const onConfirm = async () => {
  if (!props.industryId) {
    XrkMessage.warning('缺少工业信息，无法保存');
    return;
  }

  const selectedRows = pickerRows.value.filter(row => selectedIds.has(row.id));

  // 硬性校验：区间高低、必填等有问题则不能保存
  for (const row of selectedRows) {
    if (!validateRow(row, true)) {
      XrkMessage.warning('请检查已选场景的价格区间（须由低到高）');
      return;
    }
  }

  const hasOutOfPlatform = selectedRows.some(row =>
    isOutOfPlatformRange(row.minText, row.maxText, [
      row.platformPriceLower,
      row.platformPriceUpper
    ])
  );

  if (hasOutOfPlatform) {
    try {
      await XrkMessageBox.confirm(
        '您设置的价格已超出平台市场指导价格区间。平台市场指导价格仅供参考，不影响您自主定价。请确认是否继续按当前价格执行。',
        '提示',
        {
          confirmButtonText: '确认',
          cancelButtonText: '取消'
        }
      );
    } catch {
      return;
    }
  }

  const nextScenes = pickerRows.value.map(row => {
    const enabled = selectedIds.has(row.id);
    const isPlatformPrice =
      enabled &&
      Number(row.minText) === row.platformPriceLower &&
      Number(row.maxText) === row.platformPriceUpper;

    return {
      ...row,
      enabled,
      industryPriceLower: enabled
        ? isPlatformPrice
          ? null
          : Number(row.minText)
        : null,
      industryPriceUpper: enabled
        ? isPlatformPrice
          ? null
          : Number(row.maxText)
        : null
    };
  });

  saving.value = true;
  try {
    await saveScenes(props.industryId, nextScenes);
    emits('confirm', nextScenes);
    XrkMessage.success('已更新开通场景和工业价格');
    onCancel();
  } catch (error) {
    console.error('保存开通场景失败:', error);
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  fetchScenes();
});
</script>

<style lang="scss" scoped>
.picker-wrap {
  padding: 20px;
  min-height: 200px;
}
.picker-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.picker-tip {
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 12px;
}
.picker-count {
  color: #ff6b35;
  font-weight: 500;
}
.picker-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
.field-error {
  margin-top: 4px;
  font-size: 12px;
  color: #ef4444;
  line-height: 16px;
}
.field-warn {
  margin-top: 4px;
  font-size: 12px;
  color: #f59e0b;
  line-height: 16px;
}
</style>
